<script setup>
import { useUserStore } from "@/stores/user";
import { useThreadDisplay } from "@/composables/useThreadDisplay";
import { useTeamStore } from "@/stores/team";
import { useNavigation } from "@/composables/useNavigation";
import { useToast } from "vue-toastification";
import { useThreadsContextStore } from "@/stores/threads";
import ThreadStatusDropdown2 from "./ThreadStatusDropdown2.vue";
import CloseTicketModal from "@/components/ui-kit/modals/CloseTicketModal.vue";
import { ThreadStatusEnum } from "@/utils/constants.js";
import { useOrganizationStore } from "@/stores/organization";
import { addInteractionTagByOrganization } from "@/api/interaction_tags";
import IconPriority from "@/components/icons/IconPriority.vue";
import { linkifyText } from "@/utils/utils";

const userStore = useUserStore();
const teamStore = useTeamStore();
const threadsStore = useThreadsContextStore();
const organizationStore = useOrganizationStore();
const toast = useToast();

const thread = inject("THREAD");
const readOnlyMode = inject("READ_ONLY_MODE")

const headerContainerRef = ref(null);

const height = ref(0);

defineExpose({
  height,
});

let headerResizeObserver = null;

const measureHeaderHeight = () => {
  if (!headerContainerRef.value) return;
  height.value = headerContainerRef.value.getBoundingClientRect().height;
};

const SUMMARY_LINE_HEIGHT = 20;
const SUMMARY_MIN_HEIGHT = SUMMARY_LINE_HEIGHT * 3; // default: 3 lines
const SUMMARY_MAX_HEIGHT = SUMMARY_LINE_HEIGHT * 12;

const summaryHeight = ref(SUMMARY_MIN_HEIGHT);
const summaryContentRef = ref(null);

const canGrowSummary = ref(false);

const recomputeCanGrowSummary = () => {
  if (!summaryContentRef.value) return;
  canGrowSummary.value = summaryContentRef.value.scrollHeight > SUMMARY_MIN_HEIGHT + 1;
};

const isDraggingSummary = ref(false);
const summaryDragStartY = ref(0);
const summaryDragStartHeight = ref(0);

const startSummaryDrag = (e) => {
  isDraggingSummary.value = true;
  summaryDragStartY.value = e.clientY;
  summaryDragStartHeight.value = summaryHeight.value;
  window.addEventListener("pointermove", onSummaryDrag);
  window.addEventListener("pointerup", stopSummaryDrag);
};

const onSummaryDrag = (e) => {
  if (!isDraggingSummary.value) return;

  const diff = e.clientY - summaryDragStartY.value;
  let newHeight = summaryDragStartHeight.value + diff;

  newHeight = Math.max(SUMMARY_MIN_HEIGHT, Math.min(SUMMARY_MAX_HEIGHT, newHeight));

  summaryHeight.value = newHeight;
};

const stopSummaryDrag = () => {
  isDraggingSummary.value = false;
  window.removeEventListener("pointermove", onSummaryDrag);
  window.removeEventListener("pointerup", stopSummaryDrag);
};

const isLoading = ref(false);
const setIsLoading = (state) => {
  isLoading.value = state;
};

const { navigateTo, navigateBack } = useNavigation();
const { nameWithLocationDisplay, timeDisplay, threadCardActions } =
  useThreadDisplay(thread);

const titleInput = ref(
  thread.value.user_input_summary?.title || thread.value.thread_summary?.title
);
const isEditTitleMode = ref(false);
const editTitleInputRef = ref(null);

const toggleEditTitleMode = (state) => (isEditTitleMode.value = state);

const saveTitle = async () => {
  const payload = {
    user_input_summary: {
      ...thread.value.user_input_summary,
      title: titleInput.value,
    },
  };
  setIsLoading(true);
  try {
    await threadsStore.patchThread(thread.value._id, payload);
    toast.success("Title successfully updated!");
  } catch (error) {
    toast.error("Failed to updated title!");
  } finally {
    toggleEditTitleMode(false);
    setIsLoading(false);
  }
};

const saveDepartment = async (departmentId) => {
  const payload = { department: departmentId };

  setIsLoading(true);
  try {
    await threadsStore.patchThread(thread.value._id, payload);
    toast.success("Department successfully updated!");
  } catch (err) {
    toast.error("Failed to update department");
  } finally {
    setIsLoading(false);
  }
};

// when the dropdown selects a new option, update-status event is fired, passing the new status up
const updateThreadStatus = async (newStatus) => {
  const payload = { status: newStatus }
  // setIsLoading(true)

  try {
    await threadsStore.patchThread(thread.value._id, payload)
    toast.success("Status updated!")
  } catch (err) {
    toast.error("Failed to update status");
  } finally {
    setIsLoading(false);
  }
}

const isCloseTicketModalOpen = ref(false);

const handleTicketClose = async (sendNotification = false) => {
  const payload = { status: ThreadStatusEnum.SUPPORT_CLOSED };
  if (sendNotification) {
    payload.send_email = true;
  }
  try {
    await threadsStore.patchThread(thread.value._id, payload);
    isCloseTicketModalOpen.value = false;
    toast.success("Status updated!");
  } catch (err) {
    toast.error("Unable to close ticket. Please try again.");
  }
}

const convertToTicket = async () => {
  try {
    await threadsStore.patchToTicket(thread.value._id)
    toast.success("Ticket raised!")
  } catch(err) {
    toast.error("Could not convert to ticket")
  }

}

const saveAssignees = async (data) => {
  setIsLoading(true);
  try {
    await threadsStore.patchThread(thread.value._id, data);
    toast.success("Assignees successfully updated!");
  } catch (err) {
    toast.error("Failed to update assignees");
  } finally {
    setIsLoading(false);
  }
};

const saveTeams = async (data) => {
  setIsLoading(true);
  try {
    await threadsStore.patchThread(thread.value._id, data);
    toast.success("Teams successfully updated!");
  } catch (err) {
    toast.error("Failed to update teams");
  } finally {
    setIsLoading(false);
  }
};

// BE does logic to include or remove tag and returns the thread
const updateTags = async (tag, organizationId = null) => {
  try {
    await threadsStore.patchTags(thread.value._id, tag, organizationId)
    toast.success("tags successfully updated!");
  } catch(err) {
    toast.error("Failed to update tags")
  }
}

watch(
  thread,
  () =>
    (titleInput.value =
      thread.value.user_input_summary?.title ||
      thread.value.thread_summary?.title)
);

const summaryInput = ref("");
const editSummaryInputRef = ref(null);

watch(
  thread,
  (t) => {
    summaryInput.value =
      t.user_input_summary?.summary || t.thread_summary?.summary || "";
  },
  { immediate: true }
);

watch(summaryInput, () => nextTick(recomputeCanGrowSummary));

const saveSummary = async () => {
  const payload = {
    user_input_summary: {
      ...(thread.value.user_input_summary || {}),
      summary: summaryInput.value,
    },
  };
  setIsLoading(true);
  try {
    await threadsStore.patchThread(thread.value._id, payload);
    toast.success("Summary successfully updated!");
  } catch (error) {
    toast.error("Failed to updated summary!");
  } finally {
    setIsLoading(false);
  }
};

const isSummaryModalOpen = ref(false);
const summaryDraft = ref("");

const openSummaryModal = () => {
  if(readOnlyMode.value) return

  summaryDraft.value =
    thread.value?.user_input_summary?.summary ||
    thread.value?.thread_summary?.summary ||
    "";
  isSummaryModalOpen.value = true;

  nextTick(() => {
    editSummaryInputRef.value?.focus?.();
  });
};

const closeSummaryModal = () => {
  isSummaryModalOpen.value = false;
};

const saveSummaryFromModal = async () => {
  summaryInput.value = summaryDraft.value;
  await saveSummary();
  closeSummaryModal();
};

const onKeydown = (e) => {
  if (!isSummaryModalOpen.value) return;
  if (e.key === "Escape") closeSummaryModal();
};

onMounted(async () => {

  window.addEventListener("keydown", onKeydown)

  // fetch organization tags
  await organizationStore.fetchMyOrganizationTags()

  await nextTick();
  measureHeaderHeight();
  recomputeCanGrowSummary();
  headerResizeObserver = new ResizeObserver(() => {
    measureHeaderHeight();
    recomputeCanGrowSummary();
  });
  if (headerContainerRef.value) headerResizeObserver.observe(headerContainerRef.value);

});
onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeydown);
  window.removeEventListener("pointermove", onSummaryDrag);
  window.removeEventListener("pointerup", stopSummaryDrag);
  headerResizeObserver?.disconnect();
});
</script>

<template>
  <div
    v-if="thread"
    class="header"
    ref="headerContainerRef"
  >
    <div class="d-flex column-gap-3" ref="contentRef">
      <list-loader :show="isLoading" />
      <IconArrowLeftCircle class="back-icon align-self-start" @click="navigateBack" />
      <div class="interaction-card flex-grow-1 pe-2">
        <div class="d-flex align-items-center">
          <div
            class="d-flex justify-content-between align-items-start interaction-content flex-grow-1"
          >
            <div class="interaction-info">
              <div
                class="interaction-title text-neutral-450 d-flex align-items-center gap-2"
              >
                <span
                  class="title-label"
                  v-if="!isEditTitleMode"
                  v-b-tooltip.hover.bottom="titleInput || 'No Title'"
                  @click="toggleEditTitleMode(!readOnlyMode ? true : false)"
                >
                  {{ titleInput || "No Title" }}
                </span>
                <IconPriority v-if="!isEditTitleMode" :thread-id="thread._id" :ticket-priority-id="thread.ticket_priority_id" />
                <div v-else class="title-edit-container">
                  <input
                    ref="editTitleInputRef"
                    type="text"
                    v-model="titleInput"
                    :placeholder="'Ticket'"
                    class="w-100 form-control form-control-md edit-title-input"
                  />
                  <button
                    class="btn btn-md btn-secondary rounded-3 font-size-xs blue fw-bold save-button"
                    @click="saveTitle"
                  >
                    <span>Save</span>
                  </button>
                  <span
                    class="fw-bold text-neutral-350 font-size-xs cursor-pointer cancel-button"
                    @click="toggleEditTitleMode(false)"
                  >
                    Cancel
                  </span>
                </div>
              </div>

              <div
                class="interaction-subtitle text-neutral-300 d-flex align-items-center column-gap-1"
              >
                <span
                  v-if="thread.short_id"
                  class="text-decoration-underline"
                  v-b-tooltip.hover.bottom="thread.short_id"
                >
                  #{{ thread.short_id }}
                </span>
                <IconFrame />
                <span v-b-tooltip.hover.bottom="nameWithLocationDisplay.tooltip">
                  {{ nameWithLocationDisplay.tooltip }}
                </span>
                <IconFrame />
                <span v-b-tooltip.hover.bottom="timeDisplay.tooltip">
                  {{ timeDisplay.tooltip }}
                </span>
              </div>
            </div>

            <div
              class="d-flex align-items-center"
              :class="'my-column-gap-4'"
            >
              <div v-if="!readOnlyMode" class="tags">
                <tag
                  v-for="(tag, idx) in thread.tags"
                  :key="idx"
                  :tag="tag"
                  @remove-tag="updateTags"
                />
                <AddTagsDropdown
                  :threadTags="thread.tags"
                  @add-tag="updateTags"
                  @create-tag="updateTags"
                />
              </div>
            <div v-if="!readOnlyMode">
              <ThreadStatusDropdown2
                :status="thread.status"
                use-close-ticket-modal
                @update-status="updateThreadStatus"
                @request-close-ticket="isCloseTicketModalOpen = true"
                @convert-to-ticket="convertToTicket"
                padding-size="big"
              />
            </div>
            <div v-else class="d-flex">
              <ThreadStatusEndUserDropdown
                :status="thread.status"
                @update-status="updateThreadStatus"
                @convert-to-ticket="convertToTicket"
                padding-size="big"
              />
            </div>
              <div v-if="!readOnlyMode">
                <DepartmentDropdown
                  class="w-fit-content"
                  v-b-tooltip.hover.top="'Assigned Department'"
                  @updated="saveDepartment"
                  :dptId="thread.department"
                  :organizationId="thread.organization_id"
                  padding-size="big"
                />
              </div>
              <div v-if="!readOnlyMode" class="training-assignee">
                <assignee-dropdown
                  show-teams-tab
                  :agent-list="userStore.activeHqUsers"
                  :team-list="teamStore.validList"
                  :preselected-agents="thread.assignees"
                  :preselected-teams="thread.teams"
                  @update-assignees="saveAssignees"
                  @update-teams="saveTeams"
                />
              </div>

              <three-dots-dropdown
                :actions="threadCardActions"
                style="margin-left: 16px"
              >
                <template #MarkAsUnread-icon>
                  <IconRead />
                </template>
              </three-dots-dropdown>
            </div>
          </div>
        </div>
        <div class="summary-container column-gap-1">
          <div class="summary-block text-neutral-450">
            <span
              ref="summaryContentRef"
              class="title-label content summary-clickable"
              :style="{ height: summaryHeight + 'px' }"
              @dblclick="openSummaryModal"
              v-b-tooltip.hover.bottom="'Double click to edit summary'"
            >
              <span class="summary-title summary-margin">Summary</span>
              <IconFrame class="summary-margin" />
              <span
                v-if="summaryInput"
                class="summary-text"
                v-html="linkifyText(summaryInput, { linkText: 'LINK', linksNewTab: true })"
              />
              <template v-else>Add your summary here</template>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="canGrowSummary"
      class="summary-resize-handle"
      @pointerdown="startSummaryDrag"
    ></div>

    <Teleport to="body">
      <div
        v-if="isSummaryModalOpen"
        class="modal-backdrop-custom"
        @click.self="closeSummaryModal"
      >
        <div class="modal-card" role="dialog" aria-modal="true">
          <div class="modal-header">
            <div class="modal-title">Edit summary</div>
            <button class="btn-close" @click="closeSummaryModal" />
          </div>

          <div class="modal-body">
            <textarea
              ref="editSummaryInputRef"
              v-model="summaryDraft"
              class="form-control form-control-md modal-textarea"
              placeholder="Write a short summary…"
              rows="10"
            />
          </div>

          <div class="modal-footer">
            <button class="btn btn-light rounded-3 fw-bold text-neutral-350 font-size-xs cursor-pointer cancel-button" @click="closeSummaryModal">
              Cancel
            </button>
            <button
              class="btn btn-md btn-secondary rounded-3 font-size-xs blue fw-bold save-button"
              :disabled="isLoading"
              @click="saveSummaryFromModal"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <close-ticket-modal
      v-model="isCloseTicketModalOpen"
      @close-only="handleTicketClose()"
      @close-and-notify="handleTicketClose(true)"
      :is-empty-ticket="true"
    />
  </div>
</template>

<style scoped lang="scss">
.header {
  position: relative;
  // border: 2px solid red;
  padding: 1.5rem 4rem 0 4rem;

  .title-edit-container {
    display: flex;
    align-items: center;

    .edit-title-input {
      display: inline;
      margin: 0 10px 0 0;
      padding: 3px 5px;
      width: 70% !important;
    }

    .edit-title-input:focus {
      border-color: var(--primary-200);
      box-shadow: none;
    }

  }
}

.save-button {
  margin-right: 10px;
  background-color: var(--primary-250);
}

.back-icon {
  // border: 1px solid black;
  min-width: 40px;
  cursor: pointer;
  color: $bluish-gray-900;
}

.interaction-card {
  padding: 0 1rem 0.625rem 0.5rem;
  background-color: transparent !important;
  cursor: pointer;
  min-width: 0;
  // border-bottom: 1px solid $neutral-150;

  .circle-fill {
    width: 12px;
    height: 12px;
    margin-left: 5px;
    color: var(--primary-250);
  }

  .interaction-title {
    font-size: $font-size-md;
    color: $neutral-450;
    height: 32px;
    min-width: 0;

    span.title-label {
      line-height: 32px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }

    &.bold-text {
      font-weight: bold;
    }
  }

  .interaction-content {
    // border: 2px solid black;
    min-width: 425px;
    margin-right: 20px;
  }

  .interaction-info {
    min-width: 0;
    overflow: hidden;
  }

  .interaction-subtitle {
    font-size: 14px;
  }

  .training-assignee {
    min-width: 50px;
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.summary-container {
  margin-top: 0.25rem;
  font-size: 14px;
  color: $neutral-300;
  // max-width: 50%;
  // overflow-y: auto;
  scrollbar-width: none;

  // border: 1px solid black;
}

.summary-title {
  display: inline-block;
  text-decoration: underline;
}

.summary-block {
  display: inline-block;
}

.summary-margin {
  margin-right: 0.25rem;
}

.content {
  display: block;
  overflow-y: auto;
  line-height: 20px;
  overflow-wrap: anywhere;

  // keep inline so they flow as part of the summary text instead of
  // starting a new block.
  :deep(.summary-text .markdown),
  :deep(.summary-text .markdown p) {
    display: inline;
  }
}

.summary-resize-handle {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 10px;
  cursor: ns-resize;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    border-bottom: 2px solid var(--primary-250);
  }
}

.summary-clickable {
  cursor: pointer;
}

.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.modal-card {
  width: min(820px, 96vw);
  max-height: min(80vh, 820px);
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.25);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.modal-title {
  font-weight: 700;
  font-size: 14px;
  color: $neutral-450;
}

.modal-body {
  padding: 16px;
  overflow: auto;
}

.modal-textarea {
  width: 100%;
  resize: none;
  min-height: 240px;
  line-height: 1.4;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.tags {
  display: flex;
  // border: 1px solid black;

  // this is the same as space-x utility class
  &> :not([hidden]) ~ :not([hidden]) {
  margin-left: 0.5rem;
  }

  
  // flex-direction: row;
}
</style>
